import { Box, Stack, IconButton, InputAdornment } from '@mui/material';
import { common } from '@mui/material/colors';
import BaseNumberField from 'common/components/base-number-field';
import BaseTextField from 'common/components/base-text-field';
import RequiredLabel from 'common/components/required-label';
import { styleInputNumber, styleInput } from 'common/constants';
import { useDynamicQuestionMatrix } from 'common/hooks/use-dynamic-question-matrix';
import React, { useEffect, useRef } from 'react';
import { IQuestionBaseProps } from 'types/campaign-design';
import { createStyleSheet } from 'utils/helpers';
import QuestionDnD from './question-dnd';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';
import { useFormContext } from 'react-hook-form';
import lodash from 'lodash';
import { BaseCheckbox } from 'common/components/base-checkbox-group';

const QuestionMatrixText: React.FC<IQuestionBaseProps> = ({
    question,
    ...props
}) => {
    const { watch, setValue } = useFormContext();
    const insertMultipleAnswers = watch(`data.insertMultipleAnswers`);
    const multipleAnswersRow = watch(`data.multipleAnswersRow`);
    const multipleAnswersColumn = watch(`data.multipleAnswersColumn`);
    const [isBlurInsertRow, setIsBlurInsertRow] = React.useState(true);
    const [isBlurInsertColumn, setIsBlurInsertColumn] = React.useState(true);
    const rowFieldRef = useRef(false);
    const columnFieldRef = useRef(false);
    const {
        fields: rowFields,
        removeField: removeRowField,
        insertField: insertRowField,
        appendFields: appendRowFields,
    } = useDynamicQuestionMatrix({
        fieldName: 'data.rows',
        watchFieldName: 'data.numberOfRows',
        labelPrefix: 'Tuỳ chọn hàng',
    });

    const {
        fields: columnFields,
        removeField: removeColumnField,
        insertField: insertColumnField,
        appendFields: appendColumnFields,
    } = useDynamicQuestionMatrix({
        fieldName: 'data.columns',
        watchFieldName: 'data.numberOfColumns',
        labelPrefix: 'Tuỳ chọn cột',
    });

    const handleTextareaBlurRow = () => {
        setIsBlurInsertRow(true);
        rowFieldRef.current = false;
    };

    const handleTextareaFocusRow = () => {
        rowFieldRef.current = true;
        setIsBlurInsertRow(false);
    };

    const handleTextareaFocusColumn = () => {
        columnFieldRef.current = true;
        setIsBlurInsertColumn(false);
    };

    const handleTextareaBlurColumn = () => {
        setIsBlurInsertColumn(true);
        columnFieldRef.current = false;
    };

    useEffect(() => {
        const debounceCheckAndInsertMultipleAnswers = lodash.debounce(() => {
            if (!rowFieldRef.current && !columnFieldRef.current) {
                const rowLines = multipleAnswersRow
                    ?.split('\n')
                    ?.filter((line: string) => line.trim() !== '');
                appendRowFields(rowLines);
                setValue('data.multipleAnswersRow', '');
                const colLines = multipleAnswersColumn
                    ?.split('\n')
                    ?.filter((line: string) => line.trim() !== '');
                appendColumnFields(colLines);
                setValue('data.multipleAnswersColumn', '');
                setValue('data.insertMultipleAnswers', false);
            }
        }, 300);
        debounceCheckAndInsertMultipleAnswers();
        return () => {
            debounceCheckAndInsertMultipleAnswers.cancel();
        };
        // eslint-disable-next-line
    }, [isBlurInsertRow, isBlurInsertColumn]);

    useEffect(() => {
        columnFieldRef.current = !!insertMultipleAnswers;
        rowFieldRef.current = !!insertMultipleAnswers;
    }, [insertMultipleAnswers]);

    return (
        <Box>
            <Stack p={'0px 35px 35px 35px'} gap={1}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                    <QuestionDnD {...props} question={question} />
                    {!insertMultipleAnswers && (
                        <Box sx={{ width: '100%' }}>
                            <Box
                                display={'flex'}
                                sx={{ gap: 1, justifyContent: 'space-between' }}
                            >
                                <Box
                                    sx={{
                                        gap: 1,
                                        flexDirection: 'column',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <BaseTextField
                                        autoComplete="off"
                                        fullWidth
                                        name={`hidden_answer`}
                                        size="small"
                                        placeholder={`Row ${1 + 1}`}
                                        sx={{
                                            ...styles.answer,
                                            flex: 1,
                                            visibility: 'hidden',
                                        }}
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        }}
                                    />
                                    {!question.answerFromOtherQuestionLogic
                                        ?.id &&
                                        rowFields?.length === 0 && (
                                            <Box
                                                display={'flex'}
                                                alignItems={'center'}
                                                width={'100%'}
                                            >
                                                <IconButton
                                                    onClick={() =>
                                                        insertRowField(0)
                                                    }
                                                >
                                                    <PlusIcon />
                                                </IconButton>
                                                <Box component={'span'}>
                                                    Thêm hàng
                                                </Box>
                                            </Box>
                                        )}
                                    {rowFields?.map((row, rowIndex) => {
                                        const rowValue = watch(
                                            `data.rows.${rowIndex}.label`
                                        );
                                        return (
                                            <Box
                                                key={`${row.id}_${rowIndex}`}
                                                position={'relative'}
                                            >
                                                <BaseTextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    name={`data.rows.${rowIndex}.label`}
                                                    value={rowValue}
                                                    size="small"
                                                    placeholder={`Nhập nội dung hàng ${rowIndex + 1}`}
                                                    sx={{
                                                        ...styles.answer,
                                                        flex: 1,
                                                    }}
                                                    InputProps={{
                                                        inputProps: {
                                                            maxLength: 255,
                                                        },
                                                        startAdornment:
                                                            rowFields.length >
                                                            1 ? (
                                                                <InputAdornment position="start">
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            removeRowField(
                                                                                rowIndex
                                                                            )
                                                                        }
                                                                    >
                                                                        <MinusIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ) : null,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() =>
                                                                        insertRowField(
                                                                            rowIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <PlusIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <Box
                                    display={'flex'}
                                    flexDirection={'column'}
                                    sx={{ gap: 1, flex: 1 }}
                                >
                                    <Box
                                        display={'flex'}
                                        sx={{
                                            gap: 1,
                                            justifyContent: 'space-around',
                                        }}
                                    >
                                        {!question.answerFromOtherQuestionLogic
                                            ?.id &&
                                            columnFields?.length === 0 && (
                                                <Box
                                                    display={'flex'}
                                                    alignItems={'center'}
                                                    width={'100%'}
                                                >
                                                    <IconButton
                                                        onClick={() =>
                                                            insertColumnField(0)
                                                        }
                                                    >
                                                        <PlusIcon />
                                                    </IconButton>
                                                    <Box component={'span'}>
                                                        Thêm cột
                                                    </Box>
                                                </Box>
                                            )}
                                        {columnFields?.map(
                                            (column, colIndex) => {
                                                const colValue = watch(
                                                    `data.columns.${colIndex}.label`
                                                );
                                                return (
                                                    <Box
                                                        key={`${column.id}_${colIndex}`}
                                                        position={'relative'}
                                                    >
                                                        <BaseTextField
                                                            autoComplete="off"
                                                            fullWidth
                                                            name={`data.columns.${colIndex}.label`}
                                                            value={colValue}
                                                            size="small"
                                                            placeholder={`Nhập nội dung cột ${colIndex + 1}`}
                                                            sx={{
                                                                ...styles.answer,
                                                                flex: 1,
                                                                maxWidth:
                                                                    '200px',
                                                            }}
                                                            InputProps={{
                                                                inputProps: {
                                                                    maxLength: 255,
                                                                },
                                                                startAdornment:
                                                                    columnFields.length >
                                                                    1 ? (
                                                                        <InputAdornment position="start">
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    removeColumnField(
                                                                                        colIndex
                                                                                    )
                                                                                }
                                                                            >
                                                                                <MinusIcon />
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ) : null,
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() =>
                                                                                insertColumnField(
                                                                                    colIndex
                                                                                )
                                                                            }
                                                                        >
                                                                            <PlusIcon />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            }
                                        )}
                                    </Box>

                                    {/* Ve hinh ben trong khong can validate */}
                                    {rowFields?.map((row, rowIndex) => (
                                        <Box
                                            key={rowIndex}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                justifyContent: 'space-around',
                                                gap: 1,
                                            }}
                                        >
                                            {columnFields?.map(
                                                (column, colIndex) => (
                                                    <Box
                                                        key={`${colIndex}`}
                                                        sx={{
                                                            height: '60px',
                                                            width: '100%',
                                                            background:
                                                                common.white,
                                                            borderRadius:
                                                                '50px',
                                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                                            maxWidth: '200px',
                                                        }}
                                                    ></Box>
                                                )
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                    {insertMultipleAnswers && (
                        <Stack flexDirection={'column'} gap={3} width={'100%'}>
                            <Stack>
                                <RequiredLabel
                                    label={'Chèn nhiều đáp án theo hàng'}
                                    required={false}
                                    sx={{ fontSize: '16px', fontWeight: '500' }}
                                />
                                <BaseTextField
                                    multiline
                                    name={`data.multipleAnswersRow`}
                                    fullWidth
                                    size="small"
                                    minRows={4}
                                    placeholder=""
                                    sx={styles.multipleAnswer}
                                    onBlur={handleTextareaBlurRow}
                                    onFocus={handleTextareaFocusRow}
                                />
                            </Stack>
                            <Stack>
                                <RequiredLabel
                                    label={'Chèn nhiều đáp án theo cột'}
                                    required={false}
                                    sx={{ fontSize: '16px', fontWeight: '500' }}
                                />
                                <BaseTextField
                                    multiline
                                    name={`data.multipleAnswersColumn`}
                                    fullWidth
                                    size="small"
                                    minRows={4}
                                    placeholder=""
                                    sx={styles.multipleAnswer}
                                    onBlur={handleTextareaBlurColumn}
                                    onFocus={handleTextareaFocusColumn}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Stack>
            <Stack
                flexDirection={'row'}
                flexWrap={'wrap'}
                gap={4}
                p={'35px'}
                sx={{
                    borderTop: `2px dashed var(--color-primary)`,
                }}
            >
                <Stack gap={'10px'}>
                    <RequiredLabel
                        label={`Chèn nhiều đáp án cùng lúc`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseCheckbox
                        name={`data.insertMultipleAnswers`}
                        label={``}
                    />
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'} display={'none'}>
                    <RequiredLabel
                        label="Số hàng"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfRows`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'} display={'none'}>
                    <RequiredLabel
                        label="Số cột"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfColumns`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>
            </Stack>
        </Box>
    );
};

const styles = createStyleSheet({
    answer: {
        '& .MuiInputBase-root': {
            height: '60px',
            borderRadius: '50px',
            background: common.white,
        },
    },
    limitSize: {
        '& .MuiInputBase-root': {
            height: '24px',
            background: common.white,
        },
    },
    multipleAnswer: {
        ...styleInput,
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            padding: '13px 29px 13px 29px',
            height: 'auto',
            fontSize: '17px',
            borderRadius: '5px',
        },
    },
});
export default QuestionMatrixText;
